import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { MainLayout } from "@/layouts/MainLayout";
import { DataTable, FilterPanel, Toast, CallAgentButton } from "@/components/common";
import type { Column, FilterConfig } from "@/components/common";
import { leadsApi, clickToCallApi } from "@/api";
import type { UserLead } from "@/types/leads.types";
import { exportToExcel, type ExportColumn } from "@/utils/exportToExcel";
import { fetchAllForExport, EXPORT_PAGE_SIZE } from "@/utils/fetchAllForExport";
import { ExcelIcon } from "@/components/icons";
import styles from "./Leads.module.css";

// Local-development convenience only: CRA statically replaces NODE_ENV, so
// these never activate in a production build.
const IS_LOCAL_DEV = process.env.NODE_ENV === "development";
const DEV_FALLBACK_AGENT_ID = "19c813b0-3584-4b29-a4ac-f5f395fce200";

const SEARCH_TYPE_OPTIONS = [
  { label: "Mobile Number", value: "number" },
  { label: "Name", value: "name" },
  { label: "User ID", value: "id" },
  { label: "Email", value: "email" },
  { label: "Agent Name", value: "agent_name" },
];

// The API still returns these fields; don't recreate them as dynamic columns.
const EXCLUDED_COLUMN_KEYS = new Set(["loan_status", "sub_status"]);

const STATIC_COLUMNS: Column<UserLead>[] = [
  { key: "user_id", label: "User ID", sortable: true, defaultVisible: true },
  {
    key: "name",
    label: "Name",
    sortable: true,
    defaultVisible: true,
    render: (_, row) =>
      [row.fname, row.mname, row.lname].filter(Boolean).join(" ") || "-",
  },
  { key: "mob_num", label: "Mobile", sortable: true, defaultVisible: true },
  { key: "email", label: "Email", sortable: true, defaultVisible: true },
  {
    key: "agent_name",
    label: "Sub Agent Name",
    sortable: true,
    defaultVisible: true,
    render: (value, row) => (
      <CallAgentButton
        agentName={value ? String(value) : undefined}
        agentNumber={row.agent_mob_num ?? undefined}
      />
    ),
  },
  {
    key: "parent_agent_name",
    label: "Parent Agent Name",
    sortable: true,
    defaultVisible: true,
    render: (value, row) => (
      <CallAgentButton
        agentName={value ? String(value) : undefined}
        agentNumber={row.parent_agent_mob_num ?? undefined}
      />
    ),
  },
  {
    key: "product_ids",
    label: "Products Applied",
    sortable: false,
    defaultVisible: true,
    render: (value) => {
      const ids = Array.isArray(value) ? value : [];
      if (ids.length === 0) return "-";
      return (
        <span style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
          {ids.map((id: string) => (
            <span
              key={id}
              style={{
                display: "inline-block",
                padding: "2px 8px",
                fontSize: "11px",
                fontWeight: 500,
                borderRadius: "12px",
                backgroundColor: "#e0e7ff",
                color: "#3730a3",
                whiteSpace: "nowrap",
              }}
            >
              {id}
            </span>
          ))}
        </span>
      );
    },
  },
  {
    key: "created_at",
    label: "Created At",
    sortable: true,
    defaultVisible: true,
    render: (value) => {
      try {
        return value ? new Date(value as string).toLocaleDateString() : "-";
      } catch {
        return String(value);
      }
    },
  },
  {
    key: "updated_at",
    label: "Updated At",
    sortable: true,
    defaultVisible: false,
    render: (value) => {
      try {
        return value ? new Date(value as string).toLocaleDateString() : "-";
      } catch {
        return String(value);
      }
    },
  },
  {
    key: "income",
    label: "Income",
    sortable: true,
    defaultVisible: false,
    render: (value) => (value ? `₹${Number(value).toLocaleString()}` : "-"),
  },
  {
    key: "occupation",
    label: "Occupation",
    sortable: true,
    defaultVisible: false,
  },
  {
    key: "actions",
    label: "Actions",
    sortable: false,
    defaultVisible: true,
    locked: true,
    render: (_, row) =>
      row.user_id ? (
        <a
          href={`https://los-prod.dailype.in/muthoot/session-link?user_id=${row.user_id}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "6px 12px",
            backgroundColor: "#ffffff",
            color: "#1565d8",
            border: "1px solid #1565d8",
            borderRadius: "6px",
            textDecoration: "none",
            fontWeight: 500,
            fontSize: "12px",
            whiteSpace: "nowrap",
          }}
        >
          Start Journey
        </a>
      ) : null,
  },
];

const FILTER_CONFIGS: FilterConfig[] = [
  {
    key: "search_type",
    label: "Search Type",
    type: "select",
    options: SEARCH_TYPE_OPTIONS,
  },
  {
    key: "search_text",
    label: "Search",
    type: "text",
    placeholder: "Enter search text...",
  },
  {
    key: "date_range",
    label: "Date Range",
    type: "dateRangeField",
    dateRangeFields: [
      { label: "Created At", value: "created_at" },
      { label: "Updated At", value: "updated_at" },
    ],
  },
  {
    key: "utm_source",
    label: "UTM Source",
    type: "text",
    placeholder: "Filter by source...",
  },
  {
    key: "utm_medium",
    label: "UTM Medium",
    type: "text",
    placeholder: "Filter by medium...",
  },
  {
    key: "utm_campaign",
    label: "UTM Campaign",
    type: "text",
    placeholder: "Filter by campaign...",
  },
  {
    key: "utm_content",
    label: "UTM Content",
    type: "text",
    placeholder: "Filter by content...",
  },
  {
    key: "utm_language",
    label: "UTM Language",
    type: "text",
    placeholder: "Filter by language...",
  },
];

const UTM_FILTER_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_language",
] as const;

export const Leads: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [leads, setLeads] = useState<UserLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCreateLeadMenu, setShowCreateLeadMenu] = useState(false);

  const agentIdOverride = searchParams.get("agentId") || null;
  const agentName = searchParams.get("agentName") || null;
  const agentMobNum = searchParams.get("agentMobNum") || null;
  const agentEmail = searchParams.get("agentEmail") || null;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasMore, setHasMore] = useState<boolean | undefined>(undefined);

  const [exportLimit, setExportLimit] = useState<number>(10);
  const [exporting, setExporting] = useState(false);

  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const [filters, setFilters] = useState<Record<string, unknown>>(() => {
    const date_range = searchParams.getAll("date_range");
    return {
      date_range_field: searchParams.get("date_range_field") || null,
      date_range: date_range.length === 2 ? date_range : null,
      search_text: searchParams.get("search_text") || "",
      search_type: searchParams.get("search_type") || null,
      ...Object.fromEntries(
        UTM_FILTER_KEYS.map((k) => [k, searchParams.get(k) || ""]),
      ),
    };
  });

  // ponytail: UTM text inputs are controlled locally, committed to filters (API) after 400ms debounce
  const [utmInputs, setUtmInputs] = useState<Record<string, string>>(() =>
    Object.fromEntries(UTM_FILTER_KEYS.map((k) => [k, searchParams.get(k) || ""])),
  );
  const utmDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [searchTextInput, setSearchTextInput] = useState(
    () => searchParams.get("search_text") || ""
  );

  const [callToast, setCallToast] = useState({ show: false, message: "" });
  const [callingRowId, setCallingRowId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated && !IS_LOCAL_DEV) {
      navigate("/agent-login");
    }
  }, [isAuthenticated, navigate]);

  const commitUtmFilter = useCallback(
    (key: string, value: string) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setPage(1);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        if (value) newParams.set(key, value);
        else newParams.delete(key);
        return newParams;
      });
    },
    [setSearchParams],
  );

  const handleCallCustomer = useCallback(async (row: UserLead, callType: "ext" | "phone") => {
    // Always use the logged-in agent's ID for click-to-call,
    // not the sub-agent override, since it's the logged-in agent's phone that should ring
    const agentId =
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId");

    if (!agentId) {
      setCallToast({ show: true, message: "Agent ID not found" });
      return;
    }

    if (!row.mob_num) {
      setCallToast({ show: true, message: "Customer phone number not available" });
      return;
    }

    setCallingRowId(row.user_id);
    try {
      const response = await clickToCallApi.clickToCall({
        agent_id: agentId,
        customer_number: "91" + String(row.mob_num),
        cp_team: "sales_team",
        call_type: callType,
      });
      setCallToast({ show: true, message: response.message || "Call initiated successfully" });
    } catch {
      setCallToast({ show: true, message: "Failed to initiate call" });
    } finally {
      setCallingRowId(null);
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    const agentId =
      agentIdOverride ||
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId") ||
      // Dev-only fallback so the page can be previewed without agent login.
      (IS_LOCAL_DEV ? DEV_FALLBACK_AGENT_ID : null);

    if (!agentId) {
      setError("Agent ID not found");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const dateRangeField = (filters.date_range_field as string) || null;
      const dateRange: [string | null, string | null] =
        Array.isArray(filters.date_range) && filters.date_range.length === 2
          ? [
            filters.date_range[0] as string | null,
            filters.date_range[1] as string | null,
          ]
          : [null, null];

      const createdAtRange: [string | null, string | null] =
        dateRangeField === "created_at" ? dateRange : [null, null];
      const updatedAtRange: [string | null, string | null] =
        dateRangeField === "updated_at" ? dateRange : [null, null];

      const sortByValue: { [key: string]: 1 | -1 } | undefined = sortBy
        ? { [sortBy]: sortOrder === "asc" ? 1 : -1 }
        : undefined;

      const searchText = (filters.search_text as string) || "";
      const searchType = filters.search_type as string;
      const isAllSearch =
        (!searchType || searchType === "all") && searchText.length > 0;

      const likeFilters = Object.fromEntries(
        UTM_FILTER_KEYS.map((k) => [k, ((filters[k] as string) || "").trim()])
          .filter(([, v]) => v),
      );

      const baseRequest = {
        agent_id: agentId,
        page,
        page_size: pageSize,
        ranges: {
          created_at: createdAtRange,
          updated_at: updatedAtRange,
        },
        sort_by: sortByValue,
        like_filters: likeFilters,
      };

      if (isAllSearch) {
        const searchTargets: Array<"number" | "name" | "id" | "email"> = [
          "number",
          "name",
          "id",
          "email",
        ];
        const requests = searchTargets.map((type) =>
          leadsApi.getUsersFromAgentId({
            ...baseRequest,
            search_text: searchText,
            search_type: type,
          }),
        );

        const responses = await Promise.all(requests);
        let mergedLeads: UserLead[] = [];
        const seenUserIds = new Set<string>();
        let anyHasMore = false;
        let sawHasMoreField = false;

        responses.forEach((res) => {
          if (res.response) {
            res.response.forEach((lead) => {
              if (!seenUserIds.has(lead.user_id)) {
                seenUserIds.add(lead.user_id);
                mergedLeads.push(lead);
              }
            });
            if (res.pagination_metadata?.has_more !== undefined) {
              sawHasMoreField = true;
              if (res.pagination_metadata.has_more) anyHasMore = true;
            }
          }
        });

        if (mergedLeads.length > 0) {
          setLeads(mergedLeads);
          setHasMore(sawHasMoreField ? anyHasMore : undefined);
        } else {
          setLeads([]);
          setHasMore(false);
        }
      } else {
        const searchTypeValue:
          | "number"
          | "name"
          | "id"
          | "email"
          | "agent_name"
          | undefined = searchType
            ? (searchType as
              | "number"
              | "name"
              | "id"
              | "email"
              | "agent_name")
            : undefined;

        const request = {
          ...baseRequest,
          search_text: searchText || undefined,
          search_type: searchTypeValue,
        };

        const response = await leadsApi.getUsersFromAgentId(request);

        if (response.response) {
          setLeads(response.response);
          setHasMore(
            response.response.length > 0
              ? response.pagination_metadata?.has_more
              : false
          );
        } else {
          // Real API failure (no data payload) — surface the message instead
          // of silently rendering an empty table.
          setLeads([]);
          setHasMore(false);
          setError(response.message || "Failed to fetch leads");
        }
      }
    } catch (err) {
      setLeads([]);
      setHasMore(false);
      setError(
        err instanceof Error ? err.message : "Failed to fetch leads. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortOrder, filters, agentIdOverride]);

  useEffect(() => {
    if (isAuthenticated || IS_LOCAL_DEV) {
      fetchLeads();
    }
  }, [isAuthenticated, fetchLeads, agentIdOverride]);

  const handleSort = (key: string, order: "asc" | "desc") => {
    setSortBy(key);
    setSortOrder(order);
    setPage(1);
  };

  const handleFilterChange = (key: string, value: unknown) => {
    if (key === "search_text") {
      const text = typeof value === "string" ? value : "";
      setSearchTextInput(text);
      // When search text is cleared, also clear it from filters and URL params
      // so that reloading the page doesn't restore stale search results
      if (!text) {
        setFilters((prev) => ({ ...prev, search_text: "" }));
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("search_text");
          return newParams;
        });
      }
      return;
    }
    if ((UTM_FILTER_KEYS as readonly string[]).includes(key)) {
      const text = typeof value === "string" ? value : "";
      setUtmInputs((prev) => ({ ...prev, [key]: text }));
      if (utmDebounceRef.current) clearTimeout(utmDebounceRef.current);
      utmDebounceRef.current = setTimeout(
        () => commitUtmFilter(key, text),
        400,
      );
      return;
    }
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPage(1);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (Array.isArray(value)) {
        newParams.delete(key);
        value.forEach((v) => newParams.append(key, String(v)));
      } else if (value !== null && value !== undefined && value !== "") {
        newParams.set(key, String(value));
      } else {
        newParams.delete(key);
      }
      return newParams;
    });
  };

  const handleSearchSubmit = (_key: string, value: unknown) => {
    const text = typeof value === "string" ? value : "";
    if ((UTM_FILTER_KEYS as readonly string[]).includes(_key)) {
      // Enter in a UTM field: flush immediately, skip the debounce
      if (utmDebounceRef.current) clearTimeout(utmDebounceRef.current);
      setUtmInputs((prev) => ({ ...prev, [_key]: text }));
      commitUtmFilter(_key, text);
      return;
    }
    setFilters((prev) => ({
      ...prev,
      search_text: text,
    }));
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (text) {
        newParams.set("search_text", text);
      } else {
        newParams.delete("search_text");
      }
      return newParams;
    });
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchTextInput("");
    if (utmDebounceRef.current) clearTimeout(utmDebounceRef.current);
    setUtmInputs(Object.fromEntries(UTM_FILTER_KEYS.map((k) => [k, ""])));
    setFilters({
      date_range_field: null,
      date_range: null,
      search_text: "",
      search_type: null,
      ...Object.fromEntries(UTM_FILTER_KEYS.map((k) => [k, ""])),
    });
    setPage(1);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      ["sub_status", "loan_status", "date_range_field", "date_range", "search_text", "search_type", ...UTM_FILTER_KEYS].forEach((k) => newParams.delete(k));
      return newParams;
    });
  };

  const handleExportToExcel = async () => {
    const tableId = "leads";
    const savedConfigStr = localStorage.getItem(`table_config_${tableId}`);
    let targetColumns = allColumns;

    if (savedConfigStr) {
      try {
        const config = JSON.parse(savedConfigStr);
        if (config.visibleColumns && Array.isArray(config.visibleColumns)) {
          const visibleSet = new Set(config.visibleColumns);
          targetColumns = allColumns.filter((col) => visibleSet.has(col.key));

          if (config.columnOrder && Array.isArray(config.columnOrder)) {
            targetColumns.sort((a, b) => {
              const indexA = config.columnOrder.indexOf(a.key);
              const indexB = config.columnOrder.indexOf(b.key);
              if (indexA === -1) return 1;
              if (indexB === -1) return -1;
              return indexA - indexB;
            });
          }
        }
      } catch (e) {
        console.error(
          "Error parsing saved config for export, using default.",
          e,
        );
        targetColumns = allColumns.filter((c) => c.defaultVisible !== false);
      }
    } else {
      targetColumns = allColumns.filter((c) => c.defaultVisible !== false);
    }

    targetColumns = targetColumns.filter((col) => col.key !== "actions");

    // ponytail: always export UTM columns, even when hidden in the table view
    for (const key of UTM_FILTER_KEYS) {
      if (!targetColumns.some((c) => c.key === key)) {
        const col = allColumns.find((c) => c.key === key);
        if (col) targetColumns.push(col);
      }
    }

    setExporting(true);
    setError("");

    try {
      const agentId =
        agentIdOverride ||
        localStorage.getItem("agent_id") ||
        localStorage.getItem("agentId") ||
        // Dev-only fallback, same as fetchLeads, so export works locally.
        (IS_LOCAL_DEV ? DEV_FALLBACK_AGENT_ID : null);

      if (!agentId) throw new Error("Agent ID not found");

      const dateRangeField = (filters.date_range_field as string) || null;
      const dateRange: [string | null, string | null] =
        Array.isArray(filters.date_range) && filters.date_range.length === 2
          ? [
            filters.date_range[0] as string | null,
            filters.date_range[1] as string | null,
          ]
          : [null, null];

      const createdAtRange: [string | null, string | null] =
        dateRangeField === "created_at" ? dateRange : [null, null];
      const updatedAtRange: [string | null, string | null] =
        dateRangeField === "updated_at" ? dateRange : [null, null];

      const sortByValue: { [key: string]: 1 | -1 } | undefined = sortBy
        ? { [sortBy]: sortOrder === "asc" ? 1 : -1 }
        : undefined;

      const searchText = (filters.search_text as string) || "";
      const searchType = filters.search_type as string;
      const isAllSearch =
        (!searchType || searchType === "all") && searchText.length > 0;

      const likeFilters = Object.fromEntries(
        UTM_FILTER_KEYS.map((k) => [k, ((filters[k] as string) || "").trim()])
          .filter(([, v]) => v),
      );

      const baseRequest = {
        agent_id: agentId,
        ranges: {
          created_at: createdAtRange,
          updated_at: updatedAtRange,
        },
        sort_by: sortByValue,
        like_filters: likeFilters,
      };

      let dataToExport: UserLead[] = [];

      if (isAllSearch) {
        const searchTargets: Array<"number" | "name" | "id" | "email"> = [
          "number",
          "name",
          "id",
          "email",
        ];
        const seenUserIds = new Set<string>();

        for (const type of searchTargets) {
          const rows = await fetchAllForExport<UserLead>(
            async (page, pageSize) => {
              const res = await leadsApi.getUsersFromAgentId({
                ...baseRequest,
                page,
                page_size: pageSize,
                search_text: searchText,
                search_type: type,
              });
              return {
                rows: res.response || [],
                hasMore: res.pagination_metadata?.has_more,
              };
            },
            exportLimit,
            EXPORT_PAGE_SIZE,
          );

          rows.forEach((lead) => {
            if (!seenUserIds.has(lead.user_id)) {
              seenUserIds.add(lead.user_id);
              dataToExport.push(lead);
            }
          });
        }
      } else {
        const searchTypeValue:
          | "number"
          | "name"
          | "id"
          | "email"
          | "agent_name"
          | undefined = searchType
            ? (searchType as
              | "number"
              | "name"
              | "id"
              | "email"
              | "agent_name")
            : undefined;

        dataToExport = await fetchAllForExport<UserLead>(
          async (page, pageSize) => {
            const res = await leadsApi.getUsersFromAgentId({
              ...baseRequest,
              page,
              page_size: pageSize,
              search_text: searchText || undefined,
              search_type: searchTypeValue,
            });
            if (!res.response) {
              throw new Error(
                res.message || "Failed to fetch leads data for export",
              );
            }
            return {
              rows: res.response,
              hasMore: res.pagination_metadata?.has_more,
            };
          },
          exportLimit,
          EXPORT_PAGE_SIZE,
        );
      }

      if (dataToExport.length === 0) {
        alert("No data found to export.");
        return;
      }

      const exportColumns: ExportColumn<UserLead>[] = targetColumns.map(
        (col) => {
          if (!col.render) return { key: col.key, label: col.label };
          return {
            key: col.key,
            label: col.label,
            render: (value: unknown, row: UserLead) => {
              const rawValue = (row as unknown as Record<string, unknown>)[
                col.key
              ];
              if (col.key === "name") {
                const name = [row.fname, row.mname, row.lname]
                  .filter(Boolean)
                  .join(" ");
                return name || "-";
              }
              if (col.key === "application_status")
                return String(rawValue || "-");
              if (col.key === "income") return rawValue ? Number(rawValue) : 0;
              if (
                col.key.includes("_date") ||
                col.key.includes("created_at") ||
                col.key.includes("updated_at")
              ) {
                if (!rawValue) return "-";
                try {
                  return new Date(rawValue as string).toLocaleString();
                } catch {
                  return String(rawValue);
                }
              }
              if (rawValue === null || rawValue === undefined) return "-";
              return String(rawValue);
            },
          };
        },
      );

      exportToExcel(
        dataToExport as unknown as Record<string, unknown>[],
        exportColumns as unknown as ExportColumn<Record<string, unknown>>[],
        "leads",
      );
    } catch (err) {
      console.error(err);
      const errorMsg =
        err instanceof Error ? err.message : "Failed to export data";
      setError(errorMsg);
      alert(`Export Failed: ${errorMsg}`);
    } finally {
      setExporting(false);
    }
  };

  const handleCreateLead = (type: "allLenders" | "muthootEDI") => {
    const agentId =
      agentIdOverride ||
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId") ||
      "";

    const mobNum = agentMobNum || localStorage.getItem("agentMobNum") || "";

    if (!agentId) {
      alert("Agent ID not found");
      return;
    }

    let url = "";
    if (type === "allLenders") {
      url = `https://login.clickpe.ai/login?agentId=${agentId}${mobNum ? `&m=${mobNum}` : ""}&force_logout=true`;
    } else {
      url = `https://muthoot.clickpe.ai/?agentId=${agentId}${mobNum ? `&m=${mobNum}` : ""
        }`;
    }
    window.open(url, "_blank");
    setShowCreateLeadMenu(false);
  };

  const dynamicColumns = useMemo(() => {
    if (leads.length === 0) return [];
    const allKeys = Object.keys(leads[0] || {}) as (keyof UserLead)[];
    const definedKeys = new Set(STATIC_COLUMNS.map((col) => col.key));
    const remainingKeys = allKeys.filter(
      (key) =>
        !definedKeys.has(String(key)) && !EXCLUDED_COLUMN_KEYS.has(String(key)),
    );
    return remainingKeys.map((key) => {
      const label = String(key)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      let render:
        | ((value: unknown, row: UserLead) => React.ReactNode)
        | undefined;
      if (
        key.toString().includes("_amt") ||
        key.toString().includes("amount") ||
        key === "income"
      )
        render = (value) =>
          value ? `₹${Number(value).toLocaleString()}` : "-";
      else if (
        key.toString().includes("_date") ||
        key.toString().includes("created_at") ||
        key.toString().includes("updated_at") ||
        key === "dob"
      )
        render = (value) => {
          try {
            return value ? new Date(value as string).toLocaleDateString() : "-";
          } catch {
            return String(value);
          }
        };
      else if (key === "is_politically_exposed")
        render = (value) =>
          value === null || value === undefined ? "-" : value ? "Yes" : "No";
      else
        render = (value) =>
          value === null || value === undefined ? "-" : String(value);
      return {
        key: String(key),
        label,
        sortable: true,
        defaultVisible: false,
        render,
      } as Column<UserLead>;
    });
  }, [leads]);

  const allColumns = useMemo(
    () => [...STATIC_COLUMNS, ...dynamicColumns],
    [dynamicColumns],
  );

  if (!isAuthenticated && !IS_LOCAL_DEV) return null;

  return (
    <MainLayout>
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>
              Leads
              {agentName && (
                <span className={styles.agentContext}>({agentName})</span>
              )}
            </h1>
            {agentName && (
              <p className={styles.agentContextInfo}>
                Viewing leads for: {agentName} (ID: {agentIdOverride})
              </p>
            )}
          </div>

          <div className={styles.headerControls}>
            <div className={styles.createLeadWrapper}>
              <button
                className={styles.createLeadButton}
                onClick={() => setShowCreateLeadMenu(!showCreateLeadMenu)}
                title="Create New Lead"
              >
                + Create New Lead
              </button>
              {showCreateLeadMenu && (
                <>
                  <div
                    className={styles.menuOverlay}
                    onClick={() => setShowCreateLeadMenu(false)}
                  />
                  <div className={styles.createLeadMenu}>
                    <button
                      className={styles.menuItem}
                      onClick={() => handleCreateLead("allLenders")}
                    >
                      All lenders
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => handleCreateLead("muthootEDI")}
                    >
                      Muthoot EDI
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className={styles.exportToolbar}>
              <select
                value={exportLimit}
                onChange={(e) => setExportLimit(Number(e.target.value))}
                className={styles.limitSelect}
                title="Select number of records to export"
              >
                <option value={10}>Export 10</option>
                <option value={50}>Export 50</option>
                <option value={100}>Export 100</option>
                <option value={500}>Export 500</option>
                <option value={1000}>Export 1000</option>
                <option value={5000}>Export 5000</option>
              </select>
              <button
                className={styles.exportButton}
                onClick={() => handleExportToExcel()}
                disabled={loading || exporting || leads.length === 0}
                title="Export to Excel"
              >
                {exporting ? (
                  "Exporting..."
                ) : (
                  <>
                    <ExcelIcon size={18} />
                    Export to Excel
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}
        <FilterPanel
          filters={FILTER_CONFIGS}
          values={{
            ...filters,
            search_text: searchTextInput,
            ...utmInputs,
          }}
          onChange={handleFilterChange}
          onSearchSubmit={handleSearchSubmit}
          onReset={handleResetFilters}
        />
        <DataTable
          data={leads}
          columns={allColumns}
          loading={loading}
          tableId="leads"
          pagination={{
            page,
            pageSize,
            hasMore,
            onPageChange: setPage,
            onPageSizeChange: (newSize) => {
              setPageSize(newSize);
              setPage(1);
            },
          }}
          sorting={{ sortBy, sortOrder, onSort: handleSort }}
          onRowActions={[
            {
              label: "View",
              variant: "primary",
              onClick: (row) => {
                const params = new URLSearchParams();
                if (agentIdOverride) params.set("agentId", agentIdOverride);
                if (agentName) params.set("agentName", agentName);
                if (agentMobNum) params.set("agentMobNum", agentMobNum);
                if (agentEmail) params.set("agentEmail", agentEmail);
                const queryString = params.toString();
                navigate(
                  `/customer/${row.user_id}${queryString ? `?${queryString}` : ""}`,
                  { state: { lead: row } },
                );
              },
            },
            {
              label: callingRowId ? "Calling..." : "Phone",
              icon: "\u{1F4F1}",
              variant: "tertiary",
              onClick: (row) => handleCallCustomer(row, "phone"),
              disabled: (row) => !row.mob_num || callingRowId === row.user_id,
            },
          ]}
          emptyMessage="No leads found"
        />
        <Toast
          message={callToast.message}
          show={callToast.show}
          onClose={() => setCallToast({ show: false, message: "" })}
        />
      </div>
    </MainLayout>
  );
};
